import { Controller } from '@hotwired/stimulus'

export default class RebrandingInputCurrencyController extends Controller {
  static targets = ['input', 'button', 'display', 'buttonsContainer'];

  static values = { isMobile: Boolean, max: Number };

  connect() {
    this.resizeInput();
    this.handleInput();

    this.#broadcastChange();
  }

  handleInput(_event) {
    const numericValue = this.#sanitizeNumericValue();
    const decimalSeparatorAdded = numericValue.endsWith('.');

    const decimalValue = this.#adaptDecimalValue(numericValue, decimalSeparatorAdded);
    this.#saveInputs(decimalValue);

    if (decimalSeparatorAdded) {
      this.#positionCursorAfterDecimal();
    }

    this.#syncAmountToOthers();
  }

  handleAmountSync(event) {
    if (event.target == this.element || !event.detail) {
      return;
    }

    this.displayTarget.value = event.detail.value;
    this.handleInput();
  }

  updateInputValue(event) {
    this.selectedButton = event.currentTarget;
    this.inputTarget.value = this.selectedButton.dataset.value;
    this.refreshDisplayValue();
    this.resizeInput();
    this.#broadcastChange();
    this.displayTarget.focus();
  }

  resetInput() {
    this.inputTarget.value = null;
    this.displayTarget.value = null;
    this.displayTarget.focus();
    this.#broadcastChange();
  }

  resizeInput() {
    this.inputTarget.size = this.inputTarget.value.length || 1;
  }


  checkBackspace(event) {
    const { selectionStart, selectionEnd } = event.target;

    if (
      event.key === 'Backspace' &&
        this.inputTarget.value.length <= 1 ||
        (selectionStart !== selectionEnd)
    ) {
      this.inputTarget.value = null;
      this.displayTarget.value = null;
      event.preventDefault();
    } else if (event.key === 'Backspace') {
      let inputValue = this.#sanitizeNumericValue();
      // Map cursor position from formatted display (e.g. "1 234,56 €") to numeric value (e.g. "1234.56")
      // by counting only numeric characters before the cursor
      const numericPos = this.displayTarget.value.slice(0, selectionStart).replace(/[^0-9.,]/g, '').length;
      if (numericPos > 0) {
        // Remove the character before the cursor by concatenating what's before and after it
        inputValue = inputValue.slice(0, numericPos - 1) + inputValue.slice(numericPos);
      }
      if (inputValue.endsWith('.')) {
        // Remove decimal separator
        inputValue = inputValue.slice(0, -1);
      }
      this.inputTarget.value = inputValue;
      this.refreshDisplayValue();
      event.preventDefault();
    }

    this.displayTarget.dispatchEvent(new Event('input', { bubbles: true }));
    this.#broadcastChange();
  }

  refreshDisplayValue() {
    this.displayTarget.value = this.#formatCurrency(this.inputTarget.value);
  }

  /*
    @Capsens:
      for now it's the best solution we have to handle the mobile keyboard,
      the issue is we're inside a modal which prevents using position-fixed
      260px is the standard keyboard height, we add some more because of
      the suggested amounts height

    TODO: find a better solution
  */
  focusInput() {
    if (!this.isMobileValue) return;
    this.buttonsContainerTarget.style.paddingBottom = '275px';
  }

  blurInput() {
    if (!this.isMobileValue) return;
    this.buttonsContainerTarget.style.paddingBottom = '0px';
  }

  // Private methods

  #sanitizeNumericValue() {
    return this.displayTarget.value
      // Only keep numeric characters and decimal separator (excluding spaces, currency symbol...)
      .replace(/[^0-9.,]/g, '')
      // Replace comma with dot (proper decimal separator)
      .replace(/,/g, '.');
  }

  #adaptDecimalValue(inputValue, decimalSeparatorAdded) {
    if (decimalSeparatorAdded) {
      // Add 1st decimal to avoid invalid numeric value (e.g: transform "1." to "1.0")
      return parseInt(inputValue.slice(0, -1)).toFixed(1);
    }
    return inputValue;
  }

  #saveInputs(value) {
    let numericValue = parseFloat(value);
    if (this.maxValue > 0 && numericValue > this.maxValue) {
      numericValue = this.maxValue;
      value = numericValue.toString();
    }
    // Save numeric value with 2 decimals (e.g.: ensure saving "123.45" and not "123.450")
    this.inputTarget.value = parseFloat(value).toFixed(2);
    // Format currency (e.g.: transform "123,45" to "123.45 €")
    this.displayTarget.value = this.#formatCurrency(value);
  }

  #positionCursorAfterDecimal() {
    // Move cursor after decimal separator
    // e.g.: after typing "123,", ensure to have "123.|0 €" and not "123.0| €"
    const decimalPosition = this.displayTarget.value.indexOf(',') + 1;
    this.displayTarget.setSelectionRange(decimalPosition, decimalPosition);
  }

  #formatCurrency(value) {
    if (!value) return null;

    const parts = value.toString().split('.');
    const nbDecimalsDigits = parts[1] ? parts[1].length : 0;

    return Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      // Maximum is adapated to the number of decimals in the input
      // Minimut is not set to 0 to ensure keeping the decimal separator, e.g: "123.0" not transformed to "123"
      maximumFractionDigits: Math.min(nbDecimalsDigits, 2)
    }).format(value);
  }

  #broadcastChange() {
    var event = new Event('updateAmountInputValue', {bubbles: true});
    this.element.dispatchEvent(event);
  }

  #syncAmountToOthers() {
    var event = new CustomEvent('syncAmountInput', {bubbles: true, detail: {value: this.displayTarget.value}});
    this.element.dispatchEvent(event);
  }
}
